import dayjs from 'dayjs';

/**
 * Format a date for display
 * @param date Date to format
 * @returns Formatted date string (e.g., "Monday, 1 January 2025")
 */
export const formatDateDisplay = (date: Date | string): string => {
  return dayjs(date).format('dddd, D MMMM YYYY');
};

/**
 * Format a time for display
 * @param time Time to format
 * @returns Formatted time string (e.g., "14:30")
 */
export const formatTimeDisplay = (time: Date | string): string => {
  return dayjs(time).format('HH:mm');
};

/**
 * Format a date for URL path
 * @param date Date to format
 * @returns Formatted date string (e.g., "20250101")
 */
export const formatDatePath = (date: Date): string => {
  return dayjs(date).format('YYYYMMDD');
};

/**
 * Parse a date from URL path
 * @param dateString Date string to parse (e.g., "20250101")
 * @returns Parsed date
 */
export const parseDateFromPath = (dateString: string): Date => {
  // Parse YYYYMMDD format
  const year = parseInt(dateString.substring(0, 4));
  const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
  const day = parseInt(dateString.substring(6, 8));
  
  return new Date(year, month, day, 12, 0, 0); // Set to noon
};

/**
 * Get today's date at noon
 * @returns Today's date at noon
 */
export const getTodayAtNoon = (): Date => {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return today;
};

/**
 * Get yesterday's date at noon
 * @returns Yesterday's date at noon
 */
export const getYesterdayAtNoon = (): Date => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(12, 0, 0, 0);
  return yesterday;
};

/**
 * Get tomorrow's date at noon
 * @returns Tomorrow's date at noon
 */
export const getTomorrowAtNoon = (): Date => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(12, 0, 0, 0);
  return tomorrow;
};

/**
 * Check if a date is today
 * @param date Date to check
 * @returns True if the date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is in the past
 * @param date Date to check
 * @returns True if the date is in the past
 */
export const isPast = (date: Date): boolean => {
  return date.getTime() < new Date().getTime();
};

/**
 * Check if a date is in the future
 * @param date Date to check
 * @returns True if the date is in the future
 */
export const isFuture = (date: Date): boolean => {
  return date.getTime() > new Date().getTime();
};

/**
 * Format a date for GraphQL API
 * @param date Date to format
 * @returns Formatted date string (e.g., "2025-01-01")
 */
export const formatDateForApi = (date: Date): string => {
  return dayjs(date).format('YYYY-MM-DD');
};
